import React, { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import InstallMobileIcon from "@mui/icons-material/InstallMobile";
import { useAuth } from "../../context/AuthContext";
import useInstallPrompt from "../../hooks/useInstallPrompt";
import AuthShell from "./AuthShell";

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { canInstall, installed, isIos, promptInstall } = useInstallPrompt();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Email and password are required");
      return;
    }
    setSubmitting(true);
    try {
      const me = await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back");
      if (me?.role === "superadmin") {
        navigate("/superadmin", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.error || "Login failed. Please try again.";
      if (status === 403 && /approv|pending|await/i.test(msg)) {
        navigate("/pending");
        return;
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell>
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: "secondary.main" }}>
          Cable CRM
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          Sign in to your operator account
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          required
          autoFocus
          autoComplete="email"
          sx={{ mb: 2 }}
        />
        <TextField
          label="Password"
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          required
          autoComplete="current-password"
          sx={{ mb: 1 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((s) => !s)}
                  edge="end"
                  aria-label="toggle password visibility"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ textAlign: "right", mb: 2 }}>
          <Link component={RouterLink} to="/forgot-password" underline="hover" variant="body2">
            Forgot password?
          </Link>
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="secondary"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{ py: 1.25 }}
        >
          {submitting ? "Signing in…" : "Sign In"}
        </Button>
      </Box>

      <Typography variant="body2" sx={{ textAlign: "center", mt: 3, color: "text.secondary" }}>
        New operator?{" "}
        <Link component={RouterLink} to="/register" underline="hover" sx={{ fontWeight: 600 }}>
          Create an account
        </Link>
      </Typography>

      {!installed && canInstall ? (
        <Button
          fullWidth
          variant="outlined"
          color="secondary"
          startIcon={<InstallMobileIcon />}
          onClick={promptInstall}
          sx={{ mt: 2 }}
        >
          Install App
        </Button>
      ) : null}
      {!installed && !canInstall && isIos ? (
        <Typography variant="caption" sx={{ display: "block", textAlign: "center", mt: 2, color: "text.secondary" }}>
          On iPhone: tap Share, then "Add to Home Screen" to install.
        </Typography>
      ) : null}
    </AuthShell>
  );
};

export default LoginPage;
